import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Card,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Images/EditIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import BaseButton from "../Common/Buttons/BaseButton";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import { defaultInsightFields } from "./Constants";

const Insights = ({ workflowInsights, state, setState, handleSave }) => {
  const { questions, errMsg, insights = [], loading } = state;
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const handleInsightsSave = () => {
    const normalizedQuestions = questions.map((q) => ({
      ...q,
      name: q.key?.trim() || "",
    }));
    const hasEmpty = normalizedQuestions.some(
      (q) => !q.key?.trim() || !q.question?.trim() || !q.type?.trim()
    );
    if (hasEmpty) {
      setState((prev) => ({
        ...prev,
        errMsg: "Please fill all fields before saving",
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          errMsg: "",
        }));
      }, 3000);
      return;
    }
    let updatedInsights: any = [];

    if (editingIndex !== null) {
      updatedInsights = [...insights];
      updatedInsights[editingIndex] = normalizedQuestions[0];
      handleSave(updatedInsights, true);
    } else {
      const existingNames = new Set(insights.map((i) => i.name));
      updatedInsights = [
        ...normalizedQuestions.filter(
          (insight) => !existingNames.has(insight.name)
        ),
        ...insights,
      ];
      handleSave(updatedInsights, false);
    }
    setTimeout(() => setEditingIndex(null), 1000);
  };

  const handleChange = (e, idx, field) => {
    const updated = [...questions];
    updated[idx][field] =
      field === "name" ? e.target.value.replace(/\s+/g, "") : e.target.value;
    setState((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const addMoreColumn = (idx) => {
    if (
      questions[idx]?.key?.trim() ||
      questions[idx].question?.trim() ||
      questions[idx].type?.trim()
    ) {
      const add = {
        name: "",
        type: "",
        key: "",
        question: "",
        insightType: "",
      };

      setState((prev) => ({
        ...prev,
        questions: [add, ...prev.questions],
      }));
    } else {
      setState((prev) => ({
        ...prev,
        errMsg: "Please fill all the fields",
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          errMsg: "",
        }));
      }, 3000);
    }
  };

  const removeQuestion = (idx) => {
    setState((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  const handleEdit = (item, idx) => {
    setEditingIndex(idx);
    setState((prev) => ({
      ...prev,
      questions: [item],
    }));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setState((prev) => ({
      ...prev,
      questions: [
        { name: "", type: "", key: "", question: "", insightType: "" },
      ],
    }));
  };

  const confirmDelete = (idx) => {
    setDeleteIndex(idx);
    setShowDeletePopup(true);
  };

  const handleDelete = () => {
    if (deleteIndex !== null) {
      const updatedInsights = insights.filter((_, i) => i !== deleteIndex);
      handleSave(updatedInsights, false, true);
      setDeleteIndex(null);
      setShowDeletePopup(false);
    }
  };

  return (
    <>
      <CustomScrollbars height="120vh">
        <Box sx={{ gap: 3, pb: 2 }}>
          {questions.map((data, idx) => (
            <Box className="my-2" key={idx}>
              <Box className="flex-between-only">
                <Box flex={1}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box className="flex-column" flex={1}>
                      <Typography className="font-14-grey my-1">
                        Insight Name
                      </Typography>
                      <Select
                        displayEmpty
                        value={data.name || ""}
                        onChange={(e) => {
                          const selectedKey = e.target.value;
                          const selectedObj = workflowInsights.find(
                            (insight) => insight.key === selectedKey
                          );

                          if (selectedObj) {
                            const updated = [...questions];
                            updated[idx] = {
                              ...updated[idx],
                              name: selectedObj.key,
                              key: selectedObj.key || "",
                              type: selectedObj.type || "",
                              question: selectedObj.question || "",
                            };
                            setState((prev) => ({
                              ...prev,
                              questions: updated,
                            }));
                          } else {
                            handleChange(e, idx, "name");
                          }
                        }}
                        sx={commonStyles.typeSelect}
                        // disabled={editingIndex !== null}
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: 200, overflowY: "auto" },
                          },
                        }}
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <span style={{ color: "#888" }}>
                              Select Insight name
                            </span>
                          )
                        }
                      >
                        <MenuItem value="" disabled>
                          Select Insight name
                        </MenuItem>
                        {workflowInsights
                          ?.filter(
                            ({ key }) =>
                              key === data.name ||
                              (!questions.some(
                                (q, qIdx) => q.name === key && qIdx !== idx
                              ) &&
                                !insights.some((i) => i.name === key))
                          )
                          .map((insight, key) => (
                            <MenuItem value={insight?.key} key={key}>
                              {insight?.key}
                            </MenuItem>
                          ))}
                      </Select>
                    </Box>
                    <Box className="flex-column" flex={1}>
                      <Typography className="font-14-grey my-1">
                        Insight Type
                      </Typography>
                      <Select
                        displayEmpty
                        value={data.insightType || "custom"}
                        onChange={(e) => handleChange(e, idx, "insightType")}
                        sx={commonStyles.typeSelect}
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: 200, overflowY: "auto" },
                          },
                        }}
                      >
                        <MenuItem value="custom">Custom</MenuItem>
                        <MenuItem value="default">Default</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                  <Box className="flex-between-only">
                    <Box flex={1}>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}
                      >
                        <Box className="flex-column" flex={1}>
                          <Typography className="font-14-grey my-1">
                            Name
                          </Typography>
                          <TextField
                            placeholder="Enter exact name"
                            value={data.key}
                            onChange={(e) => handleChange(e, idx, "key")}
                            sx={{ ...commonStyles.inputField }}
                          />
                        </Box>

                        <Box className="flex-column" flex={1}>
                          <Typography className="font-14-grey my-1">
                            Type
                          </Typography>
                          <Select
                            displayEmpty
                            value={data.type}
                            onChange={(e) => handleChange(e, idx, "type")}
                            sx={commonStyles.typeSelect}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: "auto",
                                },
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select Type
                            </MenuItem>
                            <MenuItem value="string">Text</MenuItem>
                            <MenuItem value="integer">Number</MenuItem>
                            <MenuItem value="boolean">True/false</MenuItem>
                          </Select>
                        </Box>
                      </Box>

                      <Box className="flex-column">
                        <Typography className="font-14-grey my-1">
                          Question
                        </Typography>
                        <TextField
                          placeholder="Enter what you are looking from call"
                          value={data.question}
                          onChange={(e) => handleChange(e, idx, "question")}
                          multiline
                          maxRows={4}
                          sx={{ ...commonStyles.inputField, width: "100%" }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {!editingIndex && idx !== 0 && (
                  <Box className="p-2">
                    <IconButton
                      disabled={questions.length === 1}
                      onClick={() => removeQuestion(idx)}
                      sx={{ marginTop: 3.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {idx === 0 && (
                <>
                  {errMsg && (
                    <span className="font-600-red py-1">{errMsg}</span>
                  )}
                  {!editingIndex && (
                    <Box className="flex-start" sx={{ pt: 1 }}>
                      <Button
                        onClick={() => addMoreColumn(idx)}
                        variant="outlined"
                        startIcon={<AddIcon sx={{ color: "#666" }} />}
                        sx={commonStyles.addBtn}
                      >
                        Add
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </Box>
      </CustomScrollbars>
      <Box className="flex-end" sx={{ gap: 2, mt: "18px" }}>
        {editingIndex !== null && (
          <BaseButton
            title="Cancel"
            variant="outlined"
            handleClick={handleCancelEdit}
          />
        )}
        <BaseButton
          title={editingIndex !== null ? "Update" : "Save"}
          variant="contained"
          loading={loading}
          handleClick={handleInsightsSave}
        />
      </Box>
      <Typography className="font-14-grey" sx={{ my: 1 }}>
        Existing Insights
      </Typography>
      <CustomScrollbars height="70vh">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 1.5,
          }}
        >
          {insights.map((item, idx) => (
            <Card
              key={idx}
              variant="outlined"
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minWidth: 0,
                boxShadow: 0,
                borderRadius: 2,
              }}
            >
              <Typography
                fontSize={14}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexGrow: 1,
                }}
              >
                {item?.name}
              </Typography>
              {!defaultInsightFields.some((df) => df?.name === item?.name) && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(item, idx)}
                    disabled={editingIndex !== null}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => confirmDelete(idx)}
                    disabled={editingIndex !== null}
                    sx={{ ml: 0.5 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              )}
            </Card>
          ))}
        </Box>
      </CustomScrollbars>
      <ConfirmationPopup
        show={showDeletePopup}
        toggle={() => setShowDeletePopup(false)}
        handleSave={handleDelete}
        message="Are you sure you want to delete this insight?"
        heading="Delete Insight"
        Icon={ConfirmationIcon}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default Insights;
